import JSZip from 'jszip';
import {
  Bus,
  Channel,
  EffectSlot,
  LoopRange,
  Note,
  Pattern,
  PlaylistClip,
  Project,
  SamplerParams,
  Scene,
  SynthParams,
  defaultSynthParams,
  uid,
} from '../project/types';
import { SampleBank } from '../samples/SampleBank';
import { encodeWav } from '../rendering/wav';

/** Trigger a browser download for a blob. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

export function projectToJson(project: Project): string {
  return JSON.stringify(project, null, 2);
}

function safeStr(v: unknown, fallback: string): string {
  return typeof v === 'string' && v.length > 0 ? v : fallback;
}

function safeNum(v: unknown, fallback: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

function safeBool(v: unknown, fallback: boolean): boolean {
  return typeof v === 'boolean' ? v : fallback;
}

/**
 * Validate + fill in defaults for a project loaded from disk. Tolerates
 * older files missing newer fields (e.g. sampler params).
 */
export function normalizeProject(raw: unknown): Project {
  const r = raw as Record<string, unknown>;
  if (!r || typeof r !== 'object' || !Array.isArray(r.channels)) {
    throw new Error('Not a Zangle project file');
  }

  const patterns: Record<string, Pattern> = {};
  const rawPatterns = (r.patterns ?? {}) as Record<string, unknown>;
  for (const [id, pr] of Object.entries(rawPatterns)) {
    const p = (pr ?? {}) as Record<string, unknown>;
    const notes: Note[] = Array.isArray(p.notes)
      ? p.notes.map((n, i) => {
          const x = (n ?? {}) as Record<string, unknown>;
          return {
            id: safeStr(x.id, `n${id}-${i}`),
            start: Math.max(0, Math.floor(safeNum(x.start, 0))),
            length: Math.max(1, Math.floor(safeNum(x.length, 1))),
            note: Math.max(0, Math.min(127, Math.floor(safeNum(x.note, 60)))),
            velocity: Math.max(1, Math.min(127, Math.floor(safeNum(x.velocity, 100)))),
          };
        })
      : [];
    patterns[id] = {
      id,
      name: safeStr(p.name, 'Pattern'),
      length: Math.max(1, Math.floor(safeNum(p.length, 16))),
      notes,
    };
  }

  const channels: Channel[] = (r.channels as unknown[]).map((c, i) => {
    const x = (c ?? {}) as Record<string, unknown>;
    const kind =
      x.kind === 'drum' || x.kind === 'sampler' ? x.kind : 'synth';
    const synthRaw = (x.synth ?? {}) as Record<string, unknown>;
    const synth: SynthParams = { ...defaultSynthParams() };
    for (const k of Object.keys(synth) as Array<keyof SynthParams>) {
      const v = synthRaw[k];
      if (typeof v === typeof synth[k]) {
        (synth as unknown as Record<string, unknown>)[k] = v;
      }
    }
    const samplerRaw = (x.sampler ?? {}) as Record<string, unknown>;
    const sampler: SamplerParams = {
      sampleId: typeof samplerRaw.sampleId === 'string' ? samplerRaw.sampleId : null,
      rootNote: Math.max(0, Math.min(127, Math.floor(safeNum(samplerRaw.rootNote, 60)))),
      loop: safeBool(samplerRaw.loop, false),
    };
    const effectsRaw = Array.isArray(x.effects) ? (x.effects as unknown[]) : [];
    const effects: EffectSlot[] = effectsRaw.map((e, j) => {
      const s = (e ?? {}) as Record<string, unknown>;
      return {
        id: safeStr(s.id, `fx${i}-${j}`),
        type: safeStr(s.type, 'eq'),
        enabled: safeBool(s.enabled, true),
        params: (s.params ?? {}) as Record<string, number>,
      };
    });
    return {
      id: safeStr(x.id, `ch${i}-${uid()}`),
      name: safeStr(x.name, `Channel ${i + 1}`),
      kind,
      color: safeStr(x.color, '#4cc2ff'),
      synth,
      drum: { kit: 'classic' },
      sampler,
      patternId: safeStr(x.patternId, ''),
      muted: safeBool(x.muted, false),
      solo: safeBool(x.solo, false),
      volume: Math.max(0, Math.min(1.5, safeNum(x.volume, 0.9))),
      pan: Math.max(-1, Math.min(1, safeNum(x.pan, 0))),
      effects,
      sends: (x.sends ?? {}) as Record<string, number>,
    };
  });

  const busesRaw = Array.isArray(r.buses) ? (r.buses as unknown[]) : [];
  const buses: Bus[] = busesRaw.map((b, i) => {
    const x = (b ?? {}) as Record<string, unknown>;
    return {
      id: safeStr(x.id, `bus${i}-${uid()}`),
      name: safeStr(x.name, `Bus ${i + 1}`),
      volume: Math.max(0, Math.min(1.5, safeNum(x.volume, 0.9))),
      pan: Math.max(-1, Math.min(1, safeNum(x.pan, 0))),
      effects: Array.isArray(x.effects)
        ? (x.effects as EffectSlot[]).map((e, j) => ({
            id: safeStr(e.id, `bfx${i}-${j}`),
            type: safeStr(e.type, 'eq'),
            enabled: safeBool(e.enabled, true),
            params: (e.params ?? {}) as Record<string, number>,
          }))
        : [],
    };
  });

  const playlistRaw = Array.isArray(r.playlist) ? (r.playlist as unknown[]) : [];
  const playlist: PlaylistClip[] = playlistRaw.map((c, i) => {
    const x = (c ?? {}) as Record<string, unknown>;
    return {
      id: safeStr(x.id, `clip${i}-${uid()}`),
      channelId: safeStr(x.channelId, ''),
      patternId: safeStr(x.patternId, ''),
      startStep: Math.max(0, Math.floor(safeNum(x.startStep, 0))),
      length: Math.max(1, Math.floor(safeNum(x.length, 16))),
    };
  });

  const ts = (r.timeSig ?? {}) as Record<string, unknown>;
  const loop = (r.loop ?? {}) as Record<string, unknown>;
  const loopRange: LoopRange = {
    enabled: safeBool(loop.enabled, true),
    startStep: Math.max(0, Math.floor(safeNum(loop.startStep, 0))),
    endStep: Math.max(1, Math.floor(safeNum(loop.endStep, 16))),
  };

  const scenesRaw = Array.isArray(r.scenes) ? (r.scenes as unknown[]) : [];
  const scenes: Scene[] = scenesRaw.map((s, i) => {
    const x = (s ?? {}) as Record<string, unknown>;
    const cellsRaw = (x.cells ?? {}) as Record<string, unknown>;
    const cells: Record<string, string | null> = {};
    for (const [chId, patId] of Object.entries(cellsRaw)) {
      cells[chId] = typeof patId === 'string' && patId.length > 0 ? patId : null;
    }
    return {
      id: safeStr(x.id, `scene${i}-${uid()}`),
      name: safeStr(x.name, `Scene ${i + 1}`),
      cells,
    };
  });

  return {
    name: safeStr(r.name, 'Untitled'),
    bpm: Math.max(30, Math.min(300, safeNum(r.bpm, 120))),
    timeSig: {
      beats: Math.max(1, Math.min(12, Math.floor(safeNum(ts.beats, 4)))),
      unit: [2, 3, 4, 8].includes(safeNum(ts.unit, 4)) ? safeNum(ts.unit, 4) : 4,
    },
    countInBars: [0, 1, 2, 4].includes(safeNum(r.countInBars, 0))
      ? safeNum(r.countInBars, 0)
      : 0,
    loop: loopRange,
    patterns,
    channels,
    buses,
    playlist,
    scenes,
    masterVolume: Math.max(0, Math.min(1.5, safeNum(r.masterVolume, 1.0))),
  };
}

/**
 * Export project + user samples as a .zangle.zip:
 *   project.json, samples/index.json, samples/<id>.wav
 */
export async function exportZip(project: Project, bank: SampleBank): Promise<void> {
  const zip = new JSZip();
  zip.file('project.json', projectToJson(project));

  const index: Record<string, { name: string; rootNote: number }> = {};
  for (const s of bank.list()) {
    if (s.id.startsWith('builtin-')) continue;
    const chs: Float32Array[] = [];
    for (let ch = 0; ch < s.buffer.numberOfChannels; ch++) {
      chs.push(s.buffer.getChannelData(ch));
    }
    const wav = encodeWav(chs, s.buffer.sampleRate);
    zip.file(`samples/${s.id}.wav`, wav);
    index[s.id] = { name: s.name, rootNote: s.rootNote };
  }
  zip.file('samples/index.json', JSON.stringify(index, null, 2));

  const blob = await zip.generateAsync({ type: 'blob' });
  const safeName = (project.name || 'project').replace(/[^\w\- ]+/g, '').trim() || 'project';
  downloadBlob(blob, `${safeName}.zangle.zip`);
}

export interface ImportedSample {
  id: string;
  name: string;
  rootNote: number;
  wav: ArrayBuffer;
}

/**
 * Read a .zangle.zip: project.json + user samples. Decoding the WAVs is the
 * caller's job (needs an AudioContext).
 */
export async function importZip(
  file: File,
): Promise<{ project: Project; samples: ImportedSample[] }> {
  const zip = await JSZip.loadAsync(await file.arrayBuffer());
  const jsonFile = zip.file('project.json');
  if (!jsonFile) throw new Error('Not a Zangle zip (missing project.json)');
  const project = normalizeProject(JSON.parse(await jsonFile.async('string')));

  const samples: ImportedSample[] = [];
  const indexFile = zip.file('samples/index.json');
  const index = indexFile
    ? ((JSON.parse(await indexFile.async('string')) ?? {}) as Record<
        string,
        { name?: string; rootNote?: number }
      >)
    : {};
  for (const [path, entry] of Object.entries(zip.files)) {
    if (!path.startsWith('samples/') || !path.endsWith('.wav') || entry.dir) continue;
    const id = path.slice('samples/'.length, -'.wav'.length);
    samples.push({
      id,
      name: index[id]?.name ?? id,
      rootNote: index[id]?.rootNote ?? 60,
      wav: await entry.async('arraybuffer'),
    });
  }
  return { project, samples };
}